
import React, {
  memo,
  ReactElement,
  useCallback,
  useMemo,
  useState,
  useEffect
} from "react";
import {
  Grow,
  Tooltip,
  TooltipProps,
  useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import { isHTML } from "../utilities/misc";

export const ArrowTooltip = (props: TooltipProps) => {
  return (
    <Tooltip
      arrow
      {...props}
    >
      {props.children as any}
    </Tooltip>
  );
};

interface CursorTooltipProps {
  lines: string[]
  children: ReactElement,
  disableToggle?: boolean
  disableZoom?: boolean;
}

export const CursorTooltip = memo((props: CursorTooltipProps) => {
  const {lines, children, disableToggle = false, disableZoom = false} = props;

  const theme = useTheme();
  const {t} = useTranslation();

  const [hidden, setHidden] = useState(false);

  const title = useMemo(() => {
    if ((lines.length === 0) || hidden) return;

    const modifiedLines = [...lines];
    const hasLines = lines.length > 0;

    if (hasLines && !disableZoom) {
      const text = t('tooltip.hold_key_to_zoom');
      modifiedLines.push(text);
    }

    if (hasLines && !disableToggle) {
      const text = t('tooltip.toggle-tooltip');
      modifiedLines.push(text);
    }

    return modifiedLines.map((value, index) => {
      const renderAsHTML = isHTML(value);

      const text = renderAsHTML
        ? (<div dangerouslySetInnerHTML={{__html: value}}/>)
        : value;

      return (
        <div key={index} style={{padding: "1px"}}>
          {text}
        </div>
      );
    });
  }, [lines, hidden]);

  const onClose = useCallback(() => {
    setHidden(false);
  }, []);

  const {enteringScreen} = theme.transitions.duration;

  const transitionProps = {
    timeout: {
      enter: enteringScreen,
      exit: 0
    }
  };

  /** Tooltip toggle hotkey event listener */
  useEffect(() => {
    if (disableToggle) return;

    if (lines.length === 0) {
      setHidden(false);
      return;
    }

    const onHotkey = (event => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }

      if (event.which === 192) {
        setHidden(!hidden);
      }
    });

    window.addEventListener('keydown', onHotkey, false);

    return () => {
      window.removeEventListener('keydown', onHotkey, false);
    };
  }, [lines, hidden]);

  return (
    <Tooltip
      arrow
      followCursor
      title={title}
      placement="right"
      onClose={onClose}
      TransitionComponent={Grow}
      TransitionProps={transitionProps}
    >
      {children}
    </Tooltip>
  );
});
